import { useEffect, useRef } from 'react';
import {
  useGetTradeInStatus,
  useIsTradeInApplied,
  useSelectedTrade
} from '~/context/common/TradeContext';
import {
  useInventoryVehicles,
  useSearchRequestId
} from '~/context/srp/InventoryContext';
import { logInfo } from '~/utilities/loggers';

type FinanceVehicle = {
  apr: number;
  total: number;
  vehicleId: number;
};

export default function useAlertHighNetTradeIssues() {
  const selectedTrade = useSelectedTrade();
  const { isActive: isSelectedTradeActive } = useGetTradeInStatus();
  const inventory = useInventoryVehicles();
  const searchRequestId = useSearchRequestId();

  const isTradeInAppliedRef = useRef(false);
  isTradeInAppliedRef.current = useIsTradeInApplied();

  useEffect(() => {
    if (
      !isTradeInAppliedRef.current ||
      !selectedTrade ||
      !isSelectedTradeActive ||
      selectedTrade.equity <= 0
    ) {
      return;
    }

    // Vehicles whose price is less than equity & finance terms exist
    const vehicles = inventory.reduce<FinanceVehicle[]>((acc, vehicle) => {
      const { price, vehicleId } = vehicle;
      const {
        apr = 0,
        estimatedTaxesAndFees = 0,
        pricingErrorMessage,
        total = 0
      } = price ?? {};

      if (
        !pricingErrorMessage &&
        total + estimatedTaxesAndFees < selectedTrade.equity &&
        apr > 0
      ) {
        acc.push({ apr, total, vehicleId });
      }

      return acc;
    }, []);

    if (vehicles.length > 0) {
      logInfo('Vehicles with high net trade and finance terms', {
        searchRequestId,
        selectedTrade: {
          equity: selectedTrade.equity,
          id: selectedTrade.id
        },
        vehicles
      });
    }
  }, [inventory, isSelectedTradeActive, searchRequestId, selectedTrade]);
}
